import {
    pick as _pick
} from 'lodash-es';

import { QueryParamKey, TQueryParams } from './query-params.models';

import { IGame, IVidParam } from 'src/app/states/game/game.models';
import { convertFramesToSeconds } from 'src/app/containers/ea-video-player/ea-video-player.utils';

/**
 * Type guard to check if an object conforms to the TQueryParams interface structure.
 * @param obj The object to check.
 * @returns True if the object matches the TQueryParams structure, false otherwise.
 */
// eslint-disable-next-line complexity
export const isTQueryParams = (obj: unknown): obj is TQueryParams => {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    const paramsObject = obj as TQueryParams;
    const properties = Object.values(QueryParamKey);

    for (const prop of properties) {
        if (paramsObject.hasOwnProperty(prop)) {
            if (typeof paramsObject[prop] !== 'string') {
                return false;
            }
        }
    }

    const keys = Object.keys(paramsObject) as (keyof TQueryParams)[];
    for (const key of keys) {
        if (!properties.includes(key)) {
            // If the object has a property that is not in the allowed list, it fails.
            return false;
        }
    }

    return true;
};

/**
 * Receives an object and returns a new object containing only properties
 * that are valid keys defined in the QueryParamKey enum.
 *
 * @param params
 * @returns
 */
export const removeInvalidQueryParamsKeysFromObject = (params: unknown): TQueryParams => {
    // 1. Get the list of all valid string values from the enum.
    // This uses Object.values() to extract ['teamid', 'period', 'timestamp'].
    const validKeys = Object.values(QueryParamKey);

    // Initial Guard: Ensure input is a non-null object
    if (typeof params !== 'object' || params === null) {
        return {};
    }

    return _pick<TQueryParams>(params, validKeys);
};

/**
 * Receives a params object and a game and validates that the params make sense for that game. Will also add any missing query params with default values.
 * @param params
 * @param game
 * @returns
 */

/**
 * Parses and sanitizes a raw timestamp string from query parameters.
 * Expects HH:MM:SS:FF format, normalizes frame/second overflows,
 * and clamps to the video's maxFrame if exceeded.
 *
 * @param rawTimestamp The raw timestamp string in HH:MM:SS:FF format.
 * @param frameRate The frame rate of the video.
 * @param maxFrame The maximum frame number of the video (if available).
 * @returns A valid timestamp string in HH:MM:SS:FF format.
 */
export const sanitizeTimestamp = (
    rawTimestamp: string | undefined | null,
    frameRate: number,
    maxFrame: number | null | undefined
// eslint-disable-next-line complexity
): string => {
    const defaultTimestamp = '00:00:00:00';
    if (!rawTimestamp || typeof rawTimestamp !== 'string') {
        return defaultTimestamp;
    }

    const cleaned = rawTimestamp.trim();
    if (!cleaned) {
        return defaultTimestamp;
    }

    const parts = cleaned.split(':');
    if (parts.length !== 4) {
        return defaultTimestamp;
    }

    let hours = parseInt(parts[0], 10);
    let minutes = parseInt(parts[1], 10);
    let seconds = parseInt(parts[2], 10);
    let frames = parseInt(parts[3], 10);

    // If any parsed part is NaN or negative, fall back to 0
    hours = isNaN(hours) || hours < 0 ? 0 : hours;
    minutes = isNaN(minutes) || minutes < 0 ? 0 : minutes;
    seconds = isNaN(seconds) || seconds < 0 ? 0 : seconds;
    frames = isNaN(frames) || frames < 0 ? 0 : frames;

    // Normalize frames, seconds, and minutes
    if (frameRate > 0 && frames >= frameRate) {
        const extraSeconds = Math.floor(convertFramesToSeconds(frames, frameRate));
        frames = frames % frameRate;
        seconds += extraSeconds;
    }
    if (seconds >= 60) {
        const extraMinutes = Math.floor(seconds / 60);
        seconds = seconds % 60;
        minutes += extraMinutes;
    }
    if (minutes >= 60) {
        const extraHours = Math.floor(minutes / 60);
        minutes = minutes % 60;
        hours += extraHours;
    }

    // Validate against maxFrame if available
    if (maxFrame !== null && maxFrame !== undefined && maxFrame > 0 && frameRate > 0) {
        const totalFrames = ((hours * 3600) + (minutes * 60) + seconds) * frameRate + frames;
        if (totalFrames > maxFrame) {
            hours = Math.floor(maxFrame / (3600 * frameRate));
            const remainingAfterHours = maxFrame % (3600 * frameRate);
            minutes = Math.floor(remainingAfterHours / (60 * frameRate));
            const remainingAfterMinutes = remainingAfterHours % (60 * frameRate);
            seconds = Math.floor(remainingAfterMinutes / frameRate);
            frames = remainingAfterMinutes % frameRate;
        }
    }

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    const ff = String(frames).padStart(2, '0');

    return `${hh}:${mm}:${ss}:${ff}`;
};

export const validateAndUpdateQueryParamContent = (
    params: TQueryParams,
    game: IGame,
    vidParams: IVidParam[]
// eslint-disable-next-line complexity
): TQueryParams => {
    // make sure team ID is valid for this game
    const teamId = params.teamid
        && (game.homeTeamId === params.teamid || game.awayTeamId === params.teamid) ? params.teamid : game.homeTeamId;
    const period = params.period && parseInt(params.period, 10) <= vidParams.length ? params.period : '1';

    const selectedVidParam = vidParams.find(param => `${param.period}` === period);
    const frameRate = selectedVidParam?.frameRate ?? 30;
    const maxFrame = selectedVidParam?.maxFrame;

    const timestamp = sanitizeTimestamp(params.timestamp, frameRate, maxFrame);

    const validParams: TQueryParams = {
        teamid: teamId,
        period,
        timestamp
    };

    if (params.eventid) {
        validParams.eventid = params.eventid;
    }

    return validParams;
};
